// apps/hr/src/pages/hr/Reports.jsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart,
  PieChart,
  Users,
  DollarSign,
  Calendar,
  Download,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';
import { toast } from 'sonner';
import pb from '../../lib/pocketbaseClient';

const Reports = () => {
  const [stats, setStats] = useState({
    totalEmployees: 0,
    totalSalary: 0,
    attendanceRate: 0,
    leaveRate: 0,
    departmentStats: [],
    monthlyTrends: [],
  });
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));

  useEffect(() => {
    fetchReports();
  }, [month]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      // Get total employees
      const employees = await pb.collection('employees').getFullList({
        filter: 'status = "active"',
      });
      const totalEmployees = employees.length;

      // Get salary data
      const salaries = await pb.collection('salary').getFullList({
        filter: `month = "${month}"`,
      });
      const totalSalary = salaries.reduce((sum, s) => sum + (s.amount || 0), 0);

      // Get attendance data
      const attendance = await pb.collection('attendance').getFullList({
        filter: `date >= "${month}-01" && date <= "${month}-31"`,
      });
      const totalDays = new Date(new Date().getFullYear(), new Date().getMonth(), 0).getDate();
      const presentDays = attendance.filter(a => a.status === 'present').length;
      const attendanceRate = totalEmployees > 0 ? (presentDays / (totalEmployees * totalDays)) * 100 : 0;

      setStats({
        totalEmployees,
        totalSalary,
        attendanceRate: Math.round(attendanceRate),
        leaveRate: Math.round(100 - attendanceRate),
        departmentStats: [],
        monthlyTrends: [],
      });
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Reports</h1>
          <p className="text-sm text-slate-500">HR analytics and insights</p>
        </div>
        <div className="flex gap-2">
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 outline-none"
          />
          <button className="px-4 py-2 text-sm font-medium text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50 transition flex items-center gap-2">
            <Download className="w-4 h-4" /> Export
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Total Employees</p>
              <p className="text-2xl font-bold text-slate-800">{stats.totalEmployees}</p>
            </div>
            <div className="p-3 bg-indigo-100 rounded-lg">
              <Users className="w-5 h-5 text-indigo-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Monthly Salary</p>
              <p className="text-2xl font-bold text-slate-800">₹{stats.totalSalary.toLocaleString()}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Attendance Rate</p>
              <p className="text-2xl font-bold text-green-600">{stats.attendanceRate}%</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <TrendingUp className="w-5 h-5 text-blue-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Leave Rate</p>
              <p className="text-2xl font-bold text-amber-600">{stats.leaveRate}%</p>
            </div>
            <div className="p-3 bg-red-100 rounded-lg">
              <TrendingDown className="w-5 h-5 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Placeholder for charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <BarChart className="w-5 h-5 text-indigo-600" /> Department Wise
          </h3>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600 w-24">Engineering</span>
              <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-indigo-600 rounded-full" style={{ width: '75%' }} />
              </div>
              <span className="text-sm font-medium text-slate-700">75%</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600 w-24">HR</span>
              <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-green-600 rounded-full" style={{ width: '60%' }} />
              </div>
              <span className="text-sm font-medium text-slate-700">60%</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600 w-24">Marketing</span>
              <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-blue-600 rounded-full" style={{ width: '50%' }} />
              </div>
              <span className="text-sm font-medium text-slate-700">50%</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <PieChart className="w-5 h-5 text-indigo-600" /> Quick Stats
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-slate-100">
              <span className="text-sm text-slate-600">Active Employees</span>
              <span className="text-sm font-semibold text-slate-800">{stats.totalEmployees}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-slate-100">
              <span className="text-sm text-slate-600">Monthly Salary</span>
              <span className="text-sm font-semibold text-slate-800">₹{stats.totalSalary.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-slate-100">
              <span className="text-sm text-slate-600">Attendance Rate</span>
              <span className="text-sm font-semibold text-green-600">{stats.attendanceRate}%</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-slate-600">Leave Rate</span>
              <span className="text-sm font-semibold text-amber-600">{stats.leaveRate}%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;